import { Fragment } from '@tiptap/pm/model';

/** Body inline colors and font-family marks are not valid on heading/subheading. */
export function marksToStripForRestrictedInlineColor(schema) {
  return [schema.marks.fontFamilyClass, schema.marks.psTextColor].filter(Boolean);
}

/** Inline link marks are not valid on heading/subheading or inside pull quotes. */
export function marksToStripForRestrictedInlineLink(schema) {
  return [schema.marks.link].filter(Boolean);
}

/** Marks stripped when converting a paragraph to heading or subheading. */
export function marksToStripForHeadingSubheading(schema) {
  return [...marksToStripForRestrictedInlineColor(schema), ...marksToStripForRestrictedInlineLink(schema)];
}

function isRestrictedInlineLinkPos(doc, pos) {
  const $pos = doc.resolve(pos);
  for (let depth = $pos.depth; depth > 0; depth--) {
    const node = $pos.node(depth);
    if (node.type.name === 'blockquote') return true;
    if (node.type.name === 'paragraph') {
      const style = node.attrs.postTextStyle;
      if (style === 'heading' || style === 'subheading') return true;
    }
  }
  return false;
}

/**
 * Remove link marks from heading/subheading paragraphs and blockquotes.
 * @returns {boolean} true if `tr` was modified
 */
export function stripRestrictedInlineLinks(tr, doc, schema) {
  const linkType = schema.marks.link;
  if (!linkType) return false;

  const replacements = [];

  doc.descendants((node, pos) => {
    if (!node.isText) return;
    if (!node.marks.some((mark) => mark.type === linkType)) return;
    if (!isRestrictedInlineLinkPos(doc, pos)) return;

    const newMarks = node.marks.filter((mark) => mark.type !== linkType);
    replacements.push({
      pos,
      size: node.nodeSize,
      text: node.text,
      marks: newMarks,
    });
  });

  if (replacements.length === 0) return false;

  replacements.sort((a, b) => b.pos - a.pos);
  for (const { pos, size, text, marks } of replacements) {
    tr.replaceWith(pos, pos + size, schema.text(text, marks));
  }

  return true;
}

/** Ordered lists use marker color; only body text-color marks are stripped (font families allowed). */
export function marksToStripInOrderedLists(schema) {
  return [schema.marks.psTextColor].filter(Boolean);
}

export function stripMarksInRange(tr, from, to, markTypes) {
  (markTypes || []).filter(Boolean).forEach((markType) => {
    tr.removeMark(from, to, markType);
  });
  return tr;
}

export function removeMarksFromFragment(fragment, markTypes) {
  const strip = (markTypes || []).filter(Boolean);
  if (strip.length === 0) return fragment;
  const stripSet = new Set(strip);

  const children = [];
  fragment.forEach((child) => {
    if (child.isText) {
      children.push(child.mark(child.marks.filter((mark) => !stripSet.has(mark.type))));
      return;
    }

    if (child.content?.size) {
      children.push(child.copy(removeMarksFromFragment(child.content, strip)));
      return;
    }

    children.push(child);
  });

  return Fragment.fromArray(children);
}

function isInsideOrderedList(doc, pos) {
  const $pos = doc.resolve(pos);
  for (let depth = $pos.depth; depth > 0; depth--) {
    if ($pos.node(depth).type.name === 'orderedList') return true;
  }
  return false;
}

/**
 * Remove body text-color marks from all text inside ordered lists.
 * Uses per-text-node replacement (reliable vs removeMark across block boundaries).
 * @returns {boolean} true if `tr` was modified
 */
export function stripInlineMarksInOrderedLists(tr, doc, schema) {
  const stripSet = new Set(marksToStripInOrderedLists(schema));
  if (!stripSet.size) return false;

  const replacements = [];

  doc.descendants((node, pos) => {
    if (!node.isText) return;
    if (!node.marks.some((mark) => stripSet.has(mark.type))) return;
    if (!isInsideOrderedList(doc, pos)) return;

    const newMarks = node.marks.filter((mark) => !stripSet.has(mark.type));
    replacements.push({
      pos,
      size: node.nodeSize,
      text: node.text,
      marks: newMarks,
    });
  });

  if (replacements.length === 0) return false;

  replacements.sort((a, b) => b.pos - a.pos);
  for (const { pos, size, text, marks } of replacements) {
    tr.replaceWith(pos, pos + size, schema.text(text, marks));
  }

  return true;
}
